import { Button } from '@components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@components/ui/card'
import { InputPhone } from '@components/ui/input-phone'
import { Label } from '@components/ui/label'
import TextDivider from '@components/ui/text-divider'
import { useMutation } from '@connectrpc/connect-query'
import { useCurrentCountry } from '@hooks/use-current-country'
import { AuthType, useAuthType } from '@lib/auth-type'
import { parseErrorMessage } from '@lib/errors'
import { formatPhoneNumber } from '@lib/phone-number'
import { LoginView } from '@lib/views'
import { CountryCode } from 'libphonenumber-js/types.cjs'
import React, { Dispatch, FC, FormEvent, SetStateAction, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Link } from 'react-router'
import { toast } from 'sonner'
import {
  createIntermediateSession,
  getGoogleOAuthRedirectURL,
  issuePhoneNumberVerificationChallenge,
  setIntermediateSessionPhoneNumber,
} from '@gen/api/v1/api-ApiService_connectquery'

interface StartLoginViewProps {
  setView: Dispatch<SetStateAction<LoginView>>
}

const StartLoginView: FC<StartLoginViewProps> = ({ setView }) => {
  const authType = useAuthType()
  const currentCountry = useCurrentCountry()

  const createIntermediateSessionMutation = useMutation(
    createIntermediateSession,
  )
  const getGoogleOAuthRedirectURLMutation = useMutation(
    getGoogleOAuthRedirectURL,
  )
  const issuePhoneNumberVerificationChallengeMutation = useMutation(
    issuePhoneNumberVerificationChallenge,
  )
  const setIntermediateSessionPhoneNumberMutation = useMutation(
    setIntermediateSessionPhoneNumber,
  )

  const [phoneNumber, setPhoneNumber] = useState<string>('')

  const handleGoogleClick = async () => {
    try {
      // create an intermediate session
      await createIntermediateSessionMutation.mutateAsync({})

      // create an intermediate session
      const googleOAuthRedirectURLRes =
        await getGoogleOAuthRedirectURLMutation.mutateAsync({
          redirectUrl: `${window.location.origin}/google-oauth-callback`,
        })

      if (!googleOAuthRedirectURLRes.url) {
        throw new Error('No redirect URL returned')
      }

      // go to the next view
      window.location.href = googleOAuthRedirectURLRes.url
    } catch (error) {
      const message = parseErrorMessage(error)
      toast.error('Could not initiate Google sign-in', {
        description: message,
      })
    }
  }

  const handlePhoneNumberSubmit = async (e: FormEvent) => {
    e.preventDefault()

    try {
      if (!currentCountry) {
        throw new Error('Could not determine current country')
      }

      const formattedPhoneNumber = formatPhoneNumber(
        currentCountry as CountryCode,
        phoneNumber,
      )

      // create an intermediate session
      await createIntermediateSessionMutation.mutateAsync({})

      // set the phone number in the intermediate session
      await setIntermediateSessionPhoneNumberMutation.mutateAsync({
        phoneNumber: formattedPhoneNumber,
      })

      await issuePhoneNumberVerificationChallengeMutation.mutateAsync({})

      // go to the next view
      setView(LoginView.VerifyPhoneNumber)
    } catch (error) {
      const message = parseErrorMessage(error)
      toast.error('Could not initiate phone number sign-in', {
        description: message,
      })
    }
  }

  return (
    <>
      <Helmet>
        <title>
          {authType === AuthType.SignUp ? 'Sign up' : 'Log in'} | Ancho
        </title>
      </Helmet>
      <Card className="max-w-sm">
        <CardHeader>
          <CardTitle>
            {authType === AuthType.SignUp ? 'Sign up' : 'Log in'}
          </CardTitle>
          <CardDescription>
            Access your account by authenticating with one of the methods below.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            className="w-full"
            onClick={handleGoogleClick}
            variant="outline"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M22.56 12.25C22.56 11.47 22.49 10.72 22.36 10H12V14.26H17.92C17.66 15.63 16.88 16.79 15.71 17.57V20.34H19.28C21.36 18.42 22.56 15.6 22.56 12.25Z"
                fill="#4285F4"
              />
              <path
                d="M12 23C14.97 23 17.46 22.02 19.28 20.34L15.71 17.57C14.73 18.23 13.48 18.63 12 18.63C9.13999 18.63 6.70999 16.7 5.83999 14.1H2.17999V16.94C3.98999 20.53 7.69999 23 12 23Z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09C5.62 13.43 5.49 12.73 5.49 12C5.49 11.27 5.62 10.57 5.84 9.91V7.07H2.18C1.43 8.55 1 10.22 1 12C1 13.78 1.43 15.45 2.18 16.93L5.03 14.71L5.84 14.09Z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38C13.62 5.38 15.06 5.94 16.21 7.02L19.36 3.87C17.45 2.09 14.97 1 12 1C7.69999 1 3.98999 3.47 2.17999 7.07L5.83999 9.91C6.70999 7.31 9.13999 5.38 12 5.38Z"
                fill="#EA4335"
              />
            </svg>
            Continue with Google
          </Button>

          <TextDivider>Or use your phone</TextDivider>

          <form
            className="mt-4 w-full [&>input]:w-full [&>button]:w-full [&>button]:mt-2"
            onSubmit={handlePhoneNumberSubmit}
          >
            <Label>Phone number</Label>
            <InputPhone
              defaultCountry={(currentCountry as CountryCode) || ''}
              onChange={setPhoneNumber}
              type="tel"
              placeholder="Enter a phone number"
              value={phoneNumber}
            />
            <Button type="submit">Continue with phone number</Button>
          </form>
        </CardContent>
        <CardFooter className="text-sm justify-center [&>a]:underline">
          {authType === AuthType.SignUp ? (
            <>
              Already have an account?&nbsp;
              <Link to="/login">Log in</Link>
            </>
          ) : (
            <>
              Don't have an account yet?&nbsp;
              <Link to="/signup">Sign up</Link>
            </>
          )}
        </CardFooter>
      </Card>
    </>
  )
}

export default StartLoginView
